'use client';

import { useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Pencil, ImageIcon } from 'lucide-react';
import { HeroSlide, HeroLogo, HeroLogoConfig } from '@/lib/api';
import HeroSlideEditor from './HeroSlideEditor';
import HeroLogoEditor from './HeroLogoEditor';

function isHtml(str: string | null | undefined): boolean {
    if (!str) return false;
    return /<[a-z][\s\S]*>/i.test(str);
}

interface HeroSlideshowProps {
    slides: HeroSlide[];
    logos: HeroLogo[];
    logoConfig: HeroLogoConfig;
    isRoot: boolean;
    onSlidesChange: (slides: HeroSlide[]) => void;
    onLogosChange: (logos: HeroLogo[]) => void;
    onLogoConfigChange: (config: HeroLogoConfig) => void;
}

export default function HeroSlideshow({ slides, logos, logoConfig, isRoot, onSlidesChange, onLogosChange, onLogoConfigChange }: HeroSlideshowProps) {
    const locale = useLocale();
    const t = useTranslations('landing.heroSlideshow');
    const tLogos = useTranslations('landing.heroLogos');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [mode, setMode] = useState<'view' | 'editSlides' | 'editLogos'>('view');
    const [imageLoaded, setImageLoaded] = useState(false);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const resetTimer = () => {
        if (timerRef.current) clearInterval(timerRef.current);
        if (slides.length === 0 || mode !== 'view') return;

        const duration = (slides[currentIndex]?.duration_seconds ?? 7) * 1000;
        timerRef.current = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % slides.length);
        }, duration);
    };

    useEffect(() => {
        resetTimer();
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex, slides.length, mode]);

    const currentSlide = slides[currentIndex] ?? null;

    // Reset imageLoaded when slide image changes — must be before any early returns
    useEffect(() => {
        if (!currentSlide?.image_path) { setImageLoaded(true); return; }
        setImageLoaded(false);
        const img = new Image();
        img.src = currentSlide.image_path;
        img.onload = () => setImageLoaded(true);
    }, [currentSlide?.image_path]);

    if (mode === 'editSlides') {
        return (
            <HeroSlideEditor
                slides={slides}
                onChange={onSlidesChange}
                onClose={() => setMode('view')}
            />
        );
    }

    if (mode === 'editLogos') {
        return (
            <HeroLogoEditor
                logos={logos}
                config={logoConfig}
                onChange={onLogosChange}
                onConfigChange={onLogoConfigChange}
                onClose={() => setMode('view')}
            />
        );
    }

    const bgImage = currentSlide?.image_path ? `url(${currentSlide.image_path})` : undefined;
    const rawText = currentSlide ? (locale === 'pt' ? currentSlide.text_pt : currentSlide.text_en) : null;
    const isRight = currentSlide?.text_position === 'right';
    const isWaiting = !!currentSlide?.image_path && !imageLoaded;

    return (
        <section
            className={`relative h-screen overflow-hidden flex items-center px-6 md:px-10 bg-cover bg-center bg-no-repeat bg-white transition-opacity duration-700 ${isWaiting ? 'opacity-0' : 'opacity-100'}`}
            style={bgImage ? { backgroundImage: bgImage } : undefined}
        >
            {bgImage && <div className="absolute inset-0 bg-black/40" />}

            {/* Root edit buttons */}
            {isRoot && (
                <div className="absolute top-20 right-6 z-20 flex items-center gap-2">
                    <button
                        onClick={() => setMode('editLogos')}
                        className="flex items-center gap-2 px-3 py-2 bg-black/60 hover:bg-black/80 text-white text-sm font-medium transition-colors cursor-pointer"
                    >
                        <ImageIcon className="w-4 h-4" />
                        {tLogos('editLogos')}
                    </button>
                    <button
                        onClick={() => setMode('editSlides')}
                        className="flex items-center gap-2 px-3 py-2 bg-black/60 hover:bg-black/80 text-white text-sm font-medium transition-colors cursor-pointer"
                    >
                        <Pencil className="w-4 h-4" />
                        {t('editSlides')}
                    </button>
                </div>
            )}

            {/* Slide text */}
            {rawText && (
                <div className={`relative z-10 max-w-8xl mx-auto w-full flex ${isRight ? 'justify-end' : 'justify-start'}`}>
                    <div className="max-w-xl">
                        {isHtml(rawText) ? (
                            <div className="text-white about-content" dangerouslySetInnerHTML={{ __html: rawText }} />
                        ) : (
                            <p className="text-white text-lg leading-relaxed">{rawText}</p>
                        )}
                    </div>
                </div>
            )}

            {/* Logo strip */}
            {logos.length > 0 && (
                <div className={`absolute bottom-0 left-0 right-0 z-20 bg-black/30 backdrop-blur-sm px-8 py-3 flex items-center gap-6 overflow-x-auto scrollbar-hide ${logoConfig.alignment === 'left' ? 'justify-start' : logoConfig.alignment === 'right' ? 'justify-end' : 'justify-center'}`}>
                    {logos.map(logo => (
                        <img
                            key={logo.id}
                            src={logo.image_path}
                            alt=""
                            className="object-contain opacity-90 hover:opacity-100 transition-opacity shrink-0"
                            style={{ height: logoConfig.logo_height }}
                        />
                    ))}
                </div>
            )}

            {/* Progress dots — sit above logo strip */}
            {slides.length > 1 && (
                <div className={`absolute left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 ${logos.length > 0 ? 'bottom-24' : 'bottom-16'}`}>
                    {slides.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setCurrentIndex(i)}
                            className={`w-2 h-2 rounded-full transition-all cursor-pointer ${i === currentIndex ? 'bg-white w-4' : 'bg-white/50'}`}
                        />
                    ))}
                </div>
            )}
        </section>
    );
}
